// Pre-home splash screen. Owns the whole vertical scroll column:
//
//   splash-frame       (100vh)  stars + arched gradient + logo + press-any-key
//   transition-frame   (200vh)  procedural sky + cloud layers (CloudsMaterial)
//   home-slot          (100vh)  HomeScreen is mounted here via mountHome()
//
// The home is part of the same 400vh column, not a separate z-layer — so
// the whoosh translates the whole column up by -300vh and the home rises
// into view as part of the same tall illusion, rather than a splash
// peeling away over a static home behind it.
//
// Choreography (times relative to splash setup completing):
//   0.0s   fade black → stars
//   0.5s   fade in atmosphere gradient
//   1.0s   music-home.wav starts (looping)
//   2.25s  logo slam begins
//   2.76s  logo at 110% overshoot
//   3.25s  logo settled at 100%
//   3.5s   PRESS ANY KEY fades in
//   4.0s   key listener armed
//   key+   whoosh: 4s ease-in-out scroll translating the column up 300vh
//
// Audio is routed through MenuAudio — a shared THREE.js AudioListener bus
// that outlives splash dispose, so the home's ambient music keeps playing
// through the handoff. A capture-phase gesture listener resumes the
// AudioContext on the user's first interaction (Chromium's autoplay policy
// keeps it suspended until activation), so music.start() scheduled at
// MUSIC_START_AT begins audibly as soon as the first gesture lands.

import * as THREE from 'three';
import { WebGPURenderer } from 'three/webgpu';
import { MoonSkyMaterial } from '../render/materials/MoonSkyMaterial';
import { CloudsMaterial } from '../render/materials/CloudsMaterial';
import { menuAudio } from './MenuAudio';
import { t } from '../i18n';

const FADE_STARS_DUR = 0.5;
const FADE_GRAD_DUR  = 0.75;
const MUSIC_START_AT = 1.0;

// Logo slam — delayed so it doesn't crowd the music start. Still hits
// 110% overshoot at the same absolute cue (1.51s from music-start) and
// settles to 100% at 2.0s; the animation just becomes shorter and
// snappier, reading as an impact rather than a slow bloom.
const LOGO_DELAY_AFTER_MUSIC = 1.25;
const LOGO_DURATION_MS       = 750;
const LOGO_OVERSHOOT_OFFSET  = (1.51 - LOGO_DELAY_AFTER_MUSIC) / (2.0 - LOGO_DELAY_AFTER_MUSIC);

const KEY_FADE_IN_AT         = 2.5;
const KEY_FADE_IN_DURATION   = 0.5;

const WHOOSH_DUR_SEC = 2.5;

// Responsive stage — same 1920×1080 design grid the HomeScreen uses, so
// the logo + press-any-key scale identically to the menu that appears
// after it. Inside the stage everything is laid out in design-space pixels.
const STAGE_W = 1920;
const STAGE_H = 1080;

// Scroll column layout.
const SPLASH_FRAME_H_VH     = 100;
const TRANSITION_FRAME_H_VH = 200;
const HOME_SLOT_H_VH        = 100;
const SCROLL_TOTAL_VH       = SPLASH_FRAME_H_VH + TRANSITION_FRAME_H_VH + HOME_SLOT_H_VH;
const WHOOSH_TRANSLATE_VH   = SPLASH_FRAME_H_VH + TRANSITION_FRAME_H_VH;

const STYLES = `
@import url('https://fonts.googleapis.com/css2?family=Chewy&display=swap');

#splash-screen { position: fixed; inset: 0; z-index: 200; background: transparent; overflow: hidden; font-family: 'Chewy', sans-serif; }

#splash-screen .scroll { position: absolute; left: 0; top: 0; width: 100%; height: ${SCROLL_TOTAL_VH}vh; transform: translateY(0); will-change: transform; }
#splash-screen.whooshing .scroll { transform: translateY(-${WHOOSH_TRANSLATE_VH}vh); transition: transform ${WHOOSH_DUR_SEC}s cubic-bezier(0.7, 0, 0.3, 1); }

#splash-screen .frame { position: absolute; left: 0; width: 100%; overflow: hidden; }
#splash-screen .splash-frame     { top: 0;                                   height: ${SPLASH_FRAME_H_VH}vh;     background: #000; }
/* transition-frame has no CSS background — the CloudsMaterial paints the
   entire frame (sky gradient + cloud layers) via a full-size canvas.
   Top edge is pulled 2px UP into the splash-frame so a fractional-pixel
   gap from the scroll column's translateY(-300vh) animation can't let
   the page background leak through the seam during the whoosh. The
   overlapping 2px is the shader's top row (uSkyTop = #01222D), which
   sits over the splash-frame's bottom row (grad-layer = #00222D); a
   ~1-channel blue step at the seam reads as continuous sky rather than
   a visible line. Total column height is preserved because
   transition-frame grows 2px while keeping the same bottom edge. */
#splash-screen .transition-frame { top: calc(${SPLASH_FRAME_H_VH}vh - 2px);  height: calc(${TRANSITION_FRAME_H_VH}vh + 2px); background: transparent; }
#splash-screen .clouds-layer { position: absolute; inset: 0; }
#splash-screen .clouds-layer canvas { width: 100%; height: 100%; display: block; }

/* Home mount point — the HomeScreen's root element gets reparented into
   this frame via mountHome(). translateZ(0) establishes a containing
   block so the home's own position:fixed root + any :fixed descendants
   (.dev-toggle etc.) are scoped to this 100vh slot rather than the 400vh
   scroll column. overflow:visible because the home manages its own clip. */
#splash-screen .home-slot        { top: ${SPLASH_FRAME_H_VH + TRANSITION_FRAME_H_VH}vh; height: ${HOME_SLOT_H_VH}vh; background: transparent; transform: translateZ(0); overflow: visible; }

/* Stars — WebGPU skybox rendered into a full-frame canvas. Fades from
   0 → 1 once audio is ready. */
#splash-screen .stars-layer { position: absolute; inset: 0; opacity: 0; transition: opacity ${FADE_STARS_DUR}s ease; }
#splash-screen .stars-layer.on { opacity: 1; }
#splash-screen .stars-layer canvas { width: 100%; height: 100%; display: block; }

/* Arched atmosphere gradient — two layers stacked so we get the
   "dome peaks higher in the centre" look without breaking the seamless
   bottom edge:
     1. A base LINEAR gradient matching the original spec exactly. This
        guarantees every pixel of the bottom row is #00222D, so the
        splash frame connects seamlessly into the transition frame's
        top colour (#01222D).
     2. A radial OVERLAY centred at the bottom (50%, 100%) with a
        moderately wide ellipse, opaque #00222D at the centre fading to
        transparent at ~85%. The overlay adds extra darkness in a dome
        shape anchored to the bottom, so in the centre column the
        "final dark" colour bleeds upward sooner than at the edges —
        the transition isoline arches UP in the middle, exactly the
        "above the atmosphere" feel asked for. The overlay contributes
        zero to the edges of the bottom row (clouds at D≈1.0 there) but
        linear-base has already clamped those pixels to #00222D, so the
        seamless bottom survives regardless. */
#splash-screen .grad-layer {
  position: absolute; inset: 0; opacity: 0;
  transition: opacity ${FADE_GRAD_DUR}s ease;
  pointer-events: none;
  background:
    radial-gradient(ellipse 80% 55% at 50% 100%,
      rgba(0, 34, 45, 1) 0%,
      rgba(0, 34, 45, 0) 65%),
    linear-gradient(180deg,
      rgba(0, 4, 5, 0) 54.13%,
      #02050E 72.88%,
      #021318 84.9%,
      #00222D 100%);
}
#splash-screen .grad-layer.on { opacity: 1; }

/* 1920×1080 design stage (same pattern as HomeScreen). Everything inside
   is laid out in design-space pixels; JS keeps the transform-scale in
   sync with viewport dimensions so the splash sizes consistently with
   the menu that appears after it. */
#splash-screen .stage {
  position: absolute; top: 50%; left: 50%;
  width: ${STAGE_W}px; height: ${STAGE_H}px;
  transform-origin: center center;
  transform: translate(-50%, -50%) scale(1);
  pointer-events: none;
}

/* Logo — 654×634, centred horizontally in stage (left = (1920-654)/2 = 633),
   seated in the upper half. CSS keeps it at scale(0) so it's invisible
   until the WAAPI animation takes over at LOGO_DELAY_AFTER_MUSIC; the
   animation's fill:forwards holds it at scale(1) after. */
#splash-screen .logo {
  position: absolute; left: 633px; top: 100px;
  width: 654px; height: 634px;
  transform: scale(0); transform-origin: center center;
}
#splash-screen .logo img { width: 100%; height: 100%; display: block; user-select: none; pointer-events: none; }

/* Press-any-key — style verbatim from the spec (cyan gradient + inset
   shadows + -2.37° tilt). Positioned at y=870 in stage coords so it
   sits below the settled logo with a comfortable gap. */
#splash-screen .press-key {
  position: absolute; left: 50%; top: 870px;
  transform: translate(-50%, 0) rotate(-2.37deg);
  background: linear-gradient(180deg, rgba(0, 181, 234, 0.7) 0%, rgba(0, 94, 122, 0.7) 100%);
  box-shadow: 0px 9px 28px #04151A, inset 0px 3px 14px #28AED5, inset 0px -3px 8px #083E4F;
  border-radius: 20px;
  padding: 22px 60px;
  font-family: 'Chewy', sans-serif; font-weight: 400; font-size: 48px;
  color: #FFFFFF; letter-spacing: 0.03em; white-space: nowrap;
  text-shadow: 0 3px 0 rgba(0, 0, 0, 0.3);
  opacity: 0; transition: opacity ${KEY_FADE_IN_DURATION}s ease;
}
#splash-screen .press-key.on { opacity: 1; }
#splash-screen .press-key .any-key { color: #FF9728; }

`;

// Every common interaction type across desktop + mobile browsers. Listening
// on all of them means the very first gesture — whatever kind — counts
// toward resuming the AudioContext, so we don't miss the one that happens
// to be a tap instead of a pointerdown, or a keydown from a password
// manager auto-fill. Capture phase + passive so the handler runs before the
// app processes the event and doesn't interfere with scroll/touch semantics.
const AUDIO_UNLOCK_EVENTS = ['pointerdown', 'pointerup', 'touchstart', 'touchend', 'mousedown', 'click', 'keydown'] as const;
const AUDIO_UNLOCK_OPTS: AddEventListenerOptions = { capture: true, passive: true };

export class SplashScreen {
  private root: HTMLElement;
  private styleEl: HTMLStyleElement;

  private starsLayer!: HTMLElement;
  private gradLayer!: HTMLElement;
  private logoEl!: HTMLElement;
  private pressKeyEl!: HTMLElement;
  private stageEl!: HTMLElement;
  private homeSlotEl!: HTMLElement;

  /** True once the AudioContext has been resumed (capture-phase gesture
   *  listener removes itself afterwards — a single resume is all the
   *  browser needs). */
  private audioContextResumed = false;

  private renderer: WebGPURenderer | null = null;
  private scene: THREE.Scene | null = null;
  private camera: THREE.PerspectiveCamera | null = null;
  private skyMaterial: MoonSkyMaterial | null = null;
  // Second WebGPU renderer for the transition-frame clouds. Uses an
  // orthographic camera + fullscreen plane with a procedural CloudsMaterial
  // that bakes the sky gradient AND the cloud layers into one pass. This is
  // why the transition-frame's CSS background is transparent.
  private cloudsRenderer: WebGPURenderer | null = null;
  private cloudsScene: THREE.Scene | null = null;
  private cloudsCamera: THREE.OrthographicCamera | null = null;
  private cloudsMaterial: CloudsMaterial | null = null;
  private rafHandle = 0;
  private renderStart = 0;

  private disposed = false;
  private keyArmed = false;
  private keyHit = false;

  private resolveShow: (() => void) | null = null;
  private pendingTimers: number[] = [];

  /** Resolves once audio is preloaded + WebGPU is initialised, BEFORE the
   *  intro begins. Await this from the caller to sequence later heavy
   *  WebGPU work (e.g. HomeScreen's renderer + hat icons) after the
   *  splash's, reducing peak concurrent renderer count. */
  readonly setupPromise: Promise<void>;

  constructor() {
    this.styleEl = document.createElement('style');
    this.styleEl.textContent = STYLES;
    document.head.appendChild(this.styleEl);

    this.root = document.createElement('div');
    this.root.id = 'splash-screen';
    this.root.innerHTML = this.renderDom();
    document.body.appendChild(this.root);

    this.starsLayer = this.root.querySelector('.stars-layer') as HTMLElement;
    this.gradLayer  = this.root.querySelector('.grad-layer')  as HTMLElement;
    this.stageEl    = this.root.querySelector('.stage')       as HTMLElement;
    this.logoEl     = this.root.querySelector('.logo')        as HTMLElement;
    this.pressKeyEl = this.root.querySelector('.press-key')   as HTMLElement;
    this.homeSlotEl = this.root.querySelector('.home-slot')   as HTMLElement;

    this.updateStageScale();
    addEventListener('resize', this.updateStageScale);

    // Armed from construction (capture phase) so any user gesture counts
    // toward resuming the AudioContext — covering every common interaction
    // type across desktop + mobile so the first observable gesture wins,
    // not just the one that happens to match pointerdown. Capture options
    // object is shared between add/remove so the listener is cleanly
    // removable without identity mismatches.
    for (const ev of AUDIO_UNLOCK_EVENTS) {
      addEventListener(ev, this.audioUnlock, AUDIO_UNLOCK_OPTS);
    }

    this.setupPromise = Promise.all([menuAudio.preload(), this.setupWebGPU(), this.setupCloudsWebGPU()]).then(() => { /* void */ });
  }

  /** Reparent the HomeScreen's root element into the splash's home-slot
   *  frame. From this point the home is visually part of the scroll
   *  column — not a separate z-layer — and the whoosh scroll will bring
   *  it into view as the bottom of one continuous "tall illusion". */
  mountHome(el: HTMLElement): void {
    this.homeSlotEl.appendChild(el);
  }

  private renderDom(): string {
    return `
      <div class="scroll">
        <div class="frame splash-frame">
          <div class="stars-layer"><canvas></canvas></div>
          <div class="grad-layer"></div>
          <div class="stage">
            <div class="logo"><img src="/ui/floppy-splash-logo.svg" alt="Floppy Brawler" /></div>
            <div class="press-key">${t('splash.pressAnyKey')}</div>
          </div>
        </div>
        <div class="frame transition-frame">
          <div class="clouds-layer"><canvas></canvas></div>
        </div>
        <div class="frame home-slot"></div>
      </div>
    `;
  }

  /** Play the intro and return a promise that resolves at the end of the
   *  whoosh. Requires setupPromise to have resolved (await it from the
   *  caller to serialise WebGPU setup if needed). */
  async show(): Promise<void> {
    await this.setupPromise;
    if (this.disposed) return;
    return new Promise<void>((resolve) => {
      this.resolveShow = resolve;
      this.start();
    });
  }

  private async setupWebGPU(): Promise<void> {
    const canvas = this.root.querySelector('.stars-layer canvas') as HTMLCanvasElement;
    try {
      this.renderer = new WebGPURenderer({ canvas, antialias: true, alpha: false, powerPreference: 'high-performance' });
      await this.renderer.init();
    } catch (err) {
      console.warn('[SplashScreen] WebGPU init failed:', err);
      this.renderer = null;
      return;
    }
    this.renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
    this.renderer.setClearColor(0x000000, 1);

    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(55, innerWidth / innerHeight, 0.1, 1000);
    this.camera.position.set(0, 0, 0);

    this.skyMaterial = new MoonSkyMaterial({ showEarth: false, showNebula: false });
    const skyMesh = new THREE.Mesh(new THREE.SphereGeometry(400, 64, 32), this.skyMaterial);
    skyMesh.renderOrder = -1;
    this.scene.add(skyMesh);

    this.resize();
    addEventListener('resize', this.resize);
  }

  /** Set up the transition-frame cloud renderer. Separate WebGPURenderer
   *  instance because it targets a different canvas (the clouds-layer one
   *  in the transition-frame), and three.js doesn't support rendering a
   *  single WebGPURenderer to multiple canvases without swapping the
   *  drawing-buffer, which loses per-canvas state. A second context is
   *  cheap for one full-screen quad. */
  private async setupCloudsWebGPU(): Promise<void> {
    const canvas = this.root.querySelector('.clouds-layer canvas') as HTMLCanvasElement;
    try {
      this.cloudsRenderer = new WebGPURenderer({ canvas, antialias: false, alpha: false, powerPreference: 'high-performance' });
      await this.cloudsRenderer.init();
    } catch (err) {
      console.warn('[SplashScreen] clouds WebGPU init failed:', err);
      this.cloudsRenderer = null;
      return;
    }
    // Half-res for a 200vh canvas — the shader output is low-frequency
    // (soft noise + gradient) so sub-pixel detail isn't doing any work
    // and the GPU budget stays tight during the whoosh.
    this.cloudsRenderer.setPixelRatio(Math.min(devicePixelRatio, 2) * 0.6);
    this.cloudsRenderer.setClearColor(0x000000, 1);
    this.cloudsRenderer.outputColorSpace = THREE.SRGBColorSpace;

    this.cloudsScene = new THREE.Scene();
    this.cloudsCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    this.cloudsMaterial = new CloudsMaterial();
    const quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this.cloudsMaterial);
    this.cloudsScene.add(quad);

    this.resizeClouds();
    // Precompile so the first render frame doesn't race the noise shader
    // pipeline — mirror the HomeScreen's compileAsync safety.
    await this.cloudsRenderer.compileAsync(this.cloudsScene, this.cloudsCamera);
  }

  private resize = (): void => {
    if (this.renderer && this.camera) {
      const w = innerWidth, h = innerHeight;
      this.renderer.setSize(w, h, false);
      this.camera.aspect = w / h;
      this.camera.updateProjectionMatrix();
    }
    this.resizeClouds();
  };

  /** Drawing-buffer size for the clouds canvas. The canvas CSS size is
   *  100% × 100% of the 200vh transition frame, so its rendered size is
   *  viewport-wide × 2·viewport-tall. Aspect is (w / h) so shader noise
   *  frequency stays consistent across viewport ratios. */
  private resizeClouds = (): void => {
    if (!this.cloudsRenderer || !this.cloudsMaterial) return;
    const w = innerWidth;
    const h = innerHeight * (TRANSITION_FRAME_H_VH / 100);
    this.cloudsRenderer.setSize(w, h, false);
    this.cloudsMaterial.setAspect(w / h);
  };

  /** Uniform scale to fit the 1920×1080 design stage inside the viewport
   *  (letterboxed, same formula HomeScreen uses). Keeps the logo +
   *  press-any-key pixel-accurate to the design mock on any aspect. */
  private updateStageScale = (): void => {
    if (!this.stageEl) return;
    const s = Math.min(innerWidth / STAGE_W, innerHeight / STAGE_H);
    this.stageEl.style.transform = `translate(-50%, -50%) scale(${s})`;
  };

  private start(): void {
    this.renderStart = performance.now() / 1000;
    this.renderLoop();

    const at = (sec: number, fn: () => void): void => {
      const id = setTimeout(fn, sec * 1000) as unknown as number;
      this.pendingTimers.push(id);
    };

    requestAnimationFrame(() => { if (!this.disposed) this.starsLayer.classList.add('on'); });
    at(FADE_STARS_DUR, () => this.gradLayer.classList.add('on'));

    // Kick off home music at the design cue. The AudioContext may still be
    // suspended (user hasn't gestured yet); Music.start() schedules the
    // source and it plays from t=0 of the track on the first user gesture
    // that resumes the context. The capture-phase audioUnlock listener
    // below does that resume ASAP so the earliest interaction wins.
    at(MUSIC_START_AT, () => menuAudio.playMusic());

    // Logo fires independently of music-start so its snappy 750ms slam
    // lands on the 1.51s music cue without a sluggish bloom leading in.
    at(MUSIC_START_AT + LOGO_DELAY_AFTER_MUSIC, () => this.animateLogo());

    at(MUSIC_START_AT + KEY_FADE_IN_AT, () => this.pressKeyEl.classList.add('on'));
    at(MUSIC_START_AT + KEY_FADE_IN_AT + KEY_FADE_IN_DURATION, () => this.armKeyListener());
  }

  private removeAudioUnlockListeners(): void {
    for (const ev of AUDIO_UNLOCK_EVENTS) {
      removeEventListener(ev, this.audioUnlock, AUDIO_UNLOCK_OPTS);
    }
  }

  /** Called synchronously in the user-gesture task so AudioContext.resume()
   *  rides the transient user-activation that Chromium requires. A single
   *  resume is all we need — subsequent plays work fine on the running
   *  context — so we drop the listeners after the first hit. */
  private audioUnlock = (): void => {
    if (this.audioContextResumed) return;
    this.audioContextResumed = true;
    menuAudio.resume();
    this.removeAudioUnlockListeners();
  };

  /** 750ms slam with overshoot — accelerates into 110% (ease-in feel, reads
   *  as impact), then eases out from 110% → 100% (soft settle). No
   *  translate in the transform — the stage already handles centering,
   *  so we just drive scale. */
  private animateLogo(): void {
    this.logoEl.animate(
      [
        { transform: 'scale(0)',   offset: 0,                     easing: 'cubic-bezier(0.55, 0, 0.6, 0.2)' },
        { transform: 'scale(1.1)', offset: LOGO_OVERSHOOT_OFFSET, easing: 'cubic-bezier(0.3, 0.8, 0.4, 1)' },
        { transform: 'scale(1.0)', offset: 1 }
      ],
      { duration: LOGO_DURATION_MS, fill: 'forwards' }
    );
  }

  private armKeyListener(): void {
    if (this.keyArmed || this.disposed) return;
    this.keyArmed = true;
    addEventListener('keydown', this.onKey);
    addEventListener('pointerdown', this.onKey);
  }

  private onKey = (): void => {
    if (!this.keyArmed || this.keyHit) return;
    this.keyHit = true;
    removeEventListener('keydown', this.onKey);
    removeEventListener('pointerdown', this.onKey);
    this.beginWhoosh();
  };

  private beginWhoosh(): void {
    // Fire the whoosh SFX 0.5s into the scroll animation. MenuAudio handles
    // the gain + filter routing; this is just the one-shot trigger.
    setTimeout(() => menuAudio.playWhoosh(), 0.5 * 1000);

    // Clouds now own their own drift inside the CloudsMaterial shader
    // (per-layer time-advanced noise), so there's no per-element parallax
    // to animate here — the whole-column scroll handles the camera-upward
    // motion and the shader provides the layer-relative sideways drift.

    this.root.classList.add('whooshing');

    const id = setTimeout(() => {
      // Home music lives on MenuAudio's AudioContext — it outlives splash
      // dispose automatically, no DOM reparent needed.
      //
      // Hand the home's DOM off to <body> and nuke the splash entirely.
      // Two reasons:
      //   1. The splash root is position:fixed inset:0 z-index:200, so
      //      even with its visual content translated offscreen it keeps
      //      capturing pointer events, making the home appear frozen.
      //   2. The scroll column's transform is translateY(-300vh) with
      //      a 4s transition. On window resize the vh-based pixel value
      //      recomputes and re-transitions, producing a slow drift of
      //      everything in the splash tree toward the new viewport size.
      // Reparenting preserves the home's own state (it's the same DOM
      // node); removing the splash kills both problems at once.
      this.handoffHomeToBody();
      this.dispose();
      const r = this.resolveShow;
      this.resolveShow = null;
      if (r) r();
    }, WHOOSH_DUR_SEC * 1000) as unknown as number;
    this.pendingTimers.push(id);
  }

  /** Pull the HomeScreen's root out of the splash tree and attach it
   *  directly to <body>, so the subsequent splash removal doesn't take
   *  it with it. Safe to call if the home was never mounted. */
  private handoffHomeToBody(): void {
    if (!this.homeSlotEl) return;
    const home = this.homeSlotEl.firstElementChild as HTMLElement | null;
    if (home) document.body.appendChild(home);
  }

  private renderLoop = (): void => {
    if (this.disposed) return;
    this.rafHandle = requestAnimationFrame(this.renderLoop);
    const t = performance.now() / 1000 - this.renderStart;
    if (this.renderer && this.scene && this.camera && this.skyMaterial) {
      this.skyMaterial.setTime(t);
      this.renderer.render(this.scene, this.camera);
    }
    if (this.cloudsRenderer && this.cloudsScene && this.cloudsCamera && this.cloudsMaterial) {
      this.cloudsMaterial.setTime(t);
      this.cloudsRenderer.render(this.cloudsScene, this.cloudsCamera);
    }
    this.pollGamepadForAnyKey();
  };

  /** Treat any fresh gamepad button press (A / Start / dpad / face buttons)
   *  as the "press any key" signal. Polled inside the existing rAF so we
   *  don't spin up a second loop. Only fires while armed (post-intro) and
   *  only on edge — a button held across an earlier interaction won't re-
   *  trigger once the listener arms. */
  private prevGamepadPressed = false;
  private pollGamepadForAnyKey(): void {
    if (!this.keyArmed || this.keyHit) return;
    const pads = navigator.getGamepads?.() ?? [];
    let anyDown = false;
    for (const p of pads) {
      if (!p || !p.connected) continue;
      for (const b of p.buttons) {
        if (b && (b.pressed || b.value > 0.5)) { anyDown = true; break; }
      }
      if (anyDown) break;
    }
    if (anyDown && !this.prevGamepadPressed) this.onKey();
    this.prevGamepadPressed = anyDown;
  }

  /** Tear down the splash. Call once the user has finished with the home
   *  menu. The whoosh scroll has already translated splash-frame +
   *  transition-frame well above the viewport, so removing the DOM is
   *  invisible. The home's DOM (if still inside home-slot) gets removed
   *  with us — HomeScreen's own dispose() normally removes it first. */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    if (this.rafHandle) cancelAnimationFrame(this.rafHandle);
    for (const id of this.pendingTimers) clearTimeout(id);
    this.pendingTimers.length = 0;
    removeEventListener('resize', this.resize);
    removeEventListener('resize', this.updateStageScale);
    removeEventListener('keydown', this.onKey);
    removeEventListener('pointerdown', this.onKey);
    this.removeAudioUnlockListeners();
    this.renderer?.dispose();
    this.cloudsRenderer?.dispose();
    // Home music lives on MenuAudio's shared AudioContext and keeps playing
    // through splash dispose by design — it's the home screen's ambient
    // track. Whoosh is one-shot so there's nothing to pause; we let it
    // finish naturally.
    this.root.remove();
    this.styleEl.remove();
  }
}
